import React from 'react';
import { Button, Card, Col, Row, Space, Typography } from 'antd';
import {
  CalendarOutlined,
  CheckSquareOutlined,
  CloudOutlined,
  CompassOutlined,
  DatabaseOutlined,
  FilePdfOutlined,
  RobotOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { homeCopyEn } from '../../content/homeCopy.en';
import styles from './Home.module.css';

const { Title, Paragraph, Text } = Typography;

const featureIconMap: Record<string, React.ReactNode> = {
  multiCity: <CompassOutlined />,
  dayByDay: <CalendarOutlined />,
  weather: <CloudOutlined />,
  options: <CheckSquareOutlined />,
  aiPlanning: <RobotOutlined />,
  shareIn: <ShareAltOutlined />,
  pdf: <FilePdfOutlined />,
  localFirst: <DatabaseOutlined />,
};

const Home: React.FC = () => {
  const resolvePublicAssetUrl = (path: string): string => {
    // CRA builds with `homepage: "."` can yield `PUBLIC_URL="."` which makes relative URLs.
    // When those relative URLs are used inside CSS files (e.g., via CSS variables),
    // they can resolve relative to `/static/css/...` in production and break.
    //
    // Using an absolute URL avoids CSS-relative resolution issues.
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return new URL(normalized, window.location.href).toString();
  };

  const scrollToFeatures = (): void => {
    const el = document.getElementById('features');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.home}>
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.hero}>
            <div className={styles.heroPanel}>
              <Text className={styles.eyebrow}>{homeCopyEn.hero.eyebrow}</Text>
              <Title className={styles.heroTitle}>{homeCopyEn.hero.title}</Title>
              <Paragraph className={styles.heroSubtitle}>{homeCopyEn.hero.subtitle}</Paragraph>
              <div className={styles.heroActions}>
                <Button type="primary" size="large" onClick={scrollToFeatures} className={styles.primaryButton}>
                  {homeCopyEn.hero.primaryCta}
                </Button>
                <Button size="large" onClick={scrollToFeatures} className={styles.secondaryButton}>
                  {homeCopyEn.hero.secondaryCta}
                </Button>
              </div>
            </div>

            <div
              className={styles.heroImage}
              aria-hidden="true"
              style={{
                ['--heroImage' as unknown as string]: `url(${resolvePublicAssetUrl(homeCopyEn.hero.imageSrc)})`,
              }}
            />
          </div>
        </div>
      </section>

      <section className={styles.section} id="features">
        <div className={styles.container}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Title level={2} className={styles.sectionTitle}>
              {homeCopyEn.sections.features.title}
            </Title>
            <Paragraph className={styles.sectionSubtitle}>
              {homeCopyEn.sections.features.subtitle}
            </Paragraph>
          </Space>

          <Row gutter={[18, 18]} className={styles.grid}>
            {homeCopyEn.features.map((f) => (
              <Col xs={24} sm={12} md={12} lg={6} key={f.key}>
                <Card className={styles.glassCard} bordered={false}>
                  <div className={styles.featureIcon}>{featureIconMap[f.key]}</div>
                  <Title level={4} className={styles.cardTitle}>
                    {f.title}
                  </Title>
                  <Text className={styles.cardText}>{f.description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {homeCopyEn.imageBreaks.map((b) => (
        <section
          key={b.title}
          className={styles.imageBreak}
          style={{ ['--bgImage' as unknown as string]: `url(${resolvePublicAssetUrl(b.imageSrc)})` }}
        >
          <div className={styles.imageBreakInner}>
            <div className={styles.imageBreakPanel}>
              <Text className={styles.eyebrow}>{b.eyebrow}</Text>
              <Title level={2} className={styles.imageBreakTitle}>
                {b.title}
              </Title>
              <Paragraph className={styles.imageBreakText}>{b.description}</Paragraph>
            </div>
          </div>
        </section>
      ))}

      <section className={styles.section}>
        <div className={styles.container}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Title level={2} className={styles.sectionTitle}>
              {homeCopyEn.sections.howItWorks.title}
            </Title>
            <Paragraph className={styles.sectionSubtitle}>{homeCopyEn.sections.howItWorks.subtitle}</Paragraph>
          </Space>

          <Row gutter={[18, 18]} className={styles.grid}>
            {homeCopyEn.howItWorks.steps.map((s) => (
              <Col xs={24} md={8} key={s.title}>
                <Card className={styles.glassCard} bordered={false}>
                  <Title level={4} className={styles.cardTitle}>
                    {s.title}
                  </Title>
                  <Text className={styles.cardText}>{s.description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Title level={2} className={styles.sectionTitle}>
              {homeCopyEn.sections.workflows.title}
            </Title>
            <Paragraph className={styles.sectionSubtitle}>
              {homeCopyEn.sections.workflows.subtitle}
            </Paragraph>
          </Space>

          <Row gutter={[18, 18]} className={styles.grid}>
            {homeCopyEn.workflows.map((w) => (
              <Col xs={24} md={12} key={w.title}>
                <Card className={styles.glassCard} bordered={false}>
                  <Title level={4} className={styles.cardTitle}>
                    {w.title}
                  </Title>
                  <Text className={styles.cardText}>{w.description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.finalCta}>
            <div className={styles.finalCtaPanel}>
              <Title level={2} className={styles.finalCtaTitle}>
                {homeCopyEn.finalCta.title}
              </Title>
              <Paragraph className={styles.finalCtaSubtitle}>{homeCopyEn.finalCta.subtitle}</Paragraph>
              <Button type="primary" size="large" onClick={scrollToFeatures} className={styles.primaryButton}>
                {homeCopyEn.finalCta.cta}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
